import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT_MS = 20000;

// Treffertypen der Geocoder-API; "house" zuerst, da häufigster Adresstreffer
const KNOWN_TYPES = ["house", "street", "locality", "district", "city", "county", "state", "country"];

function mapsKey() {
  if (typeof document === "undefined") {
    return "";
  }

  // Laufzeitwert aus MapsRuntime.php, nie im Build eingefroren
  const key = document.documentElement.dataset.mapsKey;
  return typeof key === "string" ? key.trim() : "";
}

function text(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }

  return null;
}

function validLatLng(lat, lng) {
  return typeof lat === "number" && isFinite(lat) && lat >= -90 && lat <= 90
    && typeof lng === "number" && isFinite(lng) && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestId = 0;
  }

  // Bricht die laufende Suche ab und invalidiert verspätete Antworten
  cancelAutocomplete() {
    this.requestId++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    // Ohne Orts- und Straßenangabe bleibt der Feature-Name die Beschriftung
    if (!s && !g.street && g.name) {
      p.push(g.zip ? g.zip + " " + g.name : g.name);
    } else if (s) {
      p.push(s);
    }

    if (g.state && !g.street && g.state !== g.name) {
      p.push(g.state);
    }

    if (g.country && !g.street && g.country !== g.name) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Legacy-Fallback für Treffer ohne properties.type
  _legacyParts(props) {
    if (props.osm_key == "place" || props.osm_type == "R" || props.osm_value == "commercial" || props.osm_value == "information") {
      return false;
    }

    const name = text(props.name);
    const parts = {
      street: text(props.street),
      city: text(props.city),
      district: text(props.district),
      name: null
    };

    if (props.osm_key == "highway" && !parts.street) {
      parts.street = name;
    } else if (!parts.city && name) {
      parts.name = name;
    } else if (parts.city && name && name !== parts.city && props.osm_key == "boundary" && !parts.district) {
      parts.district = name;
    }

    return parts;
  }

  _typedParts(type, props) {
    // Gebietstreffer ohne Stadt und PLZ sind für die Standortauswahl zu grob
    if (type === "county" || type === "state" || type === "country") {
      return false;
    }

    const name = text(props.name);
    const parts = {
      street: text(props.street),
      city: text(props.city),
      district: text(props.district),
      name: null
    };

    switch (type) {
      case "street":
        if (!parts.street) {
          parts.street = name;
        }
        break;
      case "city":
        if (!parts.city) {
          parts.city = name;
        }
        break;
      case "district":
        if (!parts.district && name && name !== parts.city) {
          parts.district = name;
        }
        break;
      default:
        // house, locality: Name nicht als Stadt erfinden
        if (!parts.city && !parts.street && !parts.district && name) {
          parts.name = name;
        }
    }

    return parts;
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.properties || typeof l.properties !== "object") {
      return false;
    }
    if (!l.geometry || !Array.isArray(l.geometry.coordinates) || l.geometry.coordinates.length < 2) {
      return false;
    }

    // GeoJSON: [lng, lat]
    const lng = Number(l.geometry.coordinates[0]);
    const lat = Number(l.geometry.coordinates[1]);
    if (!validLatLng(lat, lng)) {
      return false;
    }

    const props = l.properties;
    const type = typeof props.type === "string" ? props.type : "";

    let parts;
    if (KNOWN_TYPES.indexOf(type) !== -1) {
      parts = this._typedParts(type, props);
    } else {
      parts = this._legacyParts(props);
    }

    if (!parts) {
      return false;
    }

    const geoaddress = {};

    if (parts.street) {
      geoaddress["street"] = parts.street;
      const number = text(props.housenumber);
      if (number) {
        geoaddress["number"] = number;
      }
    }

    const zip = text(props.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }
    if (parts.city) {
      geoaddress["city"] = parts.city;
    }
    if (parts.district && parts.district !== parts.city) {
      geoaddress["district"] = parts.district;
    }
    if (parts.name) {
      geoaddress["name"] = parts.name;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = text(props.state);
    geoaddress["country"] = text(props.country);

    geoaddress["text"] = this._geoaddressText(geoaddress);

    if (!geoaddress["text"]) {
      return false;
    }

    return geoaddress;
  }

  _features(data) {
    return data && Array.isArray(data.features) ? data.features : [];
  }

  // Liefert immer ein Array; abgebrochene oder veraltete Suchen ergeben []
  autocomplete(q) {
    this.cancelAutocomplete();

    const requestId = this.requestId;
    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();

    if (!query || !key) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      if (requestId !== this.requestId) {
        return [];
      }

      const results = [];
      this._features(res.data).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }).catch(thrown => {
      if (!this.axios.isCancel(thrown) && requestId === this.requestId) {
        console.warn("Geocoding fehlgeschlagen", thrown && thrown.message);
      }

      return [];
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  reverse(lat, lng) {
    const latNum = Number(lat);
    const lngNum = Number(lng);

    if (!validLatLng(latNum, lngNum)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    // Ohne Key kontrolliert ohne Anfrage enden; Aufrufer prüfen auf leere Antwort
    const key = mapsKey();
    if (!key) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT_MS,
      params: {
        lat: latNum,
        lon: lngNum,
        lang: "de",
        key
      }
    }).then(res => {
      const features = this._features(res.data);
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : false;
      if (!geoaddress) {
        throw new Error("invalid data");
      }

      return geoaddress;
    });
  }
}

function GeocoderFactory() {
  return depsloader.load("axios").then(axios => new Geocoder(axios));
}

export { GeocoderFactory };

export default {};
